<template>
    <div class="modal" :id="compModalIdentifier" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title text-center">
                        {{ hasEvidence ? 'Edit' : 'Create' }} {{ type.toUpperCase() }} Evidence
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body bg-light">
                    <fieldset>
                        <div class="row">
                            <div class="col-12">
                                <AdminBlock title="Details">
                                    <p class="mb-0">
                                        <strong>
                                            Job:
                                        </strong>
                                        <a :href="jobLink">
                                            {{ movement?.job?.reference }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Customer:
                                        </strong>
                                        <a :href="customerLink">
                                            {{ movement?.job?.customer?.name }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Movement:
                                        </strong>
                                        <a :href="movementLink">
                                            {{ movement?.journey }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Driver:
                                        </strong>
                                        <template v-if="movement?.driver?.name">
                                            <a :href="driverLink">
                                                {{ movement?.driver?.name }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            No Driver
                                        </template>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Vehicle Reg:
                                        </strong>
                                        <a :href="vehicleLink">
                                            {{ movement?.job?.vehicle?.reg }}
                                        </a>
                                        {{ movement?.job?.vehicle?.description }}
                                        <span style="white-space:nowrap">( {{ movement?.job?.vehicle?.vehicle_type?.type }} )</span>
                                    </p>
                                </AdminBlock>
                            </div>
                        </div>

                        <AdminBlock v-if="!hasEvidence">
                            <div class="d-flex align-items-center justify-content-between">
                                <p class="mb-0 me-2" style="font-size: 14px;">If you do not have real values for this form and just wish for it to be completed, please select this button</p>
                                <button @click="autofillEvidence()" class="btn btn-danger btn-sm">Autofill Evidence</button>
                            </div>
                        </AdminBlock>

                        <div class="row">
                            <div class="col-lg-6">
                                <AdminBlock title="Expense">
                                    <InputTextField
                                        name="print_name"
                                        label="Print Name"
                                        placeholder="Print Name"
                                        required
                                        remove-margin
                                        v-model="printName"
                                    />
                                </AdminBlock>

                                <AdminBlock title="Upload">
                                    <p class="mb-4">
                                        Please upload your Evidence here
                                    </p>

                                    <FileUpload
                                        :file-upload-route="fileUploadRoute"
                                        ref="fileUpload"
                                        multiple
                                        no-label
                                        no-margin
                                        @file-uploaded="handleFileUploaded"
                                    />
                                </AdminBlock>
                            </div>
                            <div class="col-lg-6">
                                <AdminBlock title="Explanation" remove-margin>
                                    <InputTextField
                                        name="action"
                                        label="Action"
                                        placeholder="Action"
                                        required
                                        v-model="action"
                                    />
                                    <InputTextField
                                        name="damage"
                                        label="Damage"
                                        placeholder="Damage"
                                        required
                                        v-model="damage"
                                    />
                                    <InputTextField
                                        name="starting_mileage"
                                        :label="getMileageLabel"
                                        :placeholder="getMileageLabel"
                                        required
                                        v-model="startingMileage"
                                    />
                                    <div class="">
                                        <label>
                                            <strong>
                                                {{ getFuelLabel }}
                                                <span class="text-danger">*</span>
                                            </strong>
                                        </label>
                                        <div class="w-100">
                                          <FuelGauge v-model="startingFuel"/>
                                        </div>
                                    </div>
                                </AdminBlock>
                            </div>
                        </div>

                        <template v-if="files.length > 0">
                            <div class="row">
                                <div class="col-12 mt-4">
                                <AdminBlock class="mb-0" :title="'File' + (files.length > 1 ? 's' : '')">
                                    <table class="table table-striped mb-0">
                                        <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th style="width: 115px;">Date</th>
                                            <th style="width: 150px;">Action</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="file in files" :key="file.id">
                                            <td class="align-middle">
                                                <a :href="getFileLink(file)" class="d-flex align-items-center"
                                                   target="_blank">
                                                    <i class="fa fa-file me-2"></i>
                                                    {{ file.name }}
                                                </a>
                                            </td>
                                            <td class="align-middle">
                                                {{ new Date(file.created_at).toLocaleDateString() }}
                                            </td>
                                            <td class="align-middle">
                                                <a :href="getFileLink(file)" class="btn btn-secondary btn-sm me-2" target="_blank">
                                                    View
                                                </a>
                                                <button class="btn btn-danger btn-sm" type="button"
                                                        @click="deleteFile(file)">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </AdminBlock>
                            </div>
                            </div>
                        </template>

                    </fieldset>
                </div>

                <div class="modal-footer">
                    <button type="button"
                            :id="'create_' + getModalType() + 'contact_close_button'"
                            class="btn btn-secondary"
                            data-bs-dismiss="modal">Close
                    </button>

                    <button type="button" class="btn btn-primary" @click="createNewEvidence">
                        {{ hasEvidence ? 'Update' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import axios from 'axios';
import Modal from 'bootstrap/js/dist/modal';
import { ref, computed, onMounted, watch } from 'vue';
import { errorAlert, successAlert } from "@/utils";
import AdminBlock from '@/components/movements/components/AdminBlock.vue';
import FileUpload from "@/components/movements/components/FileUpload.vue";
import FuelGauge from "@/components/movements/components/FuelGauge.vue";
import InputTextField from '@/components/movements/components/InputTextField.vue';

const props = defineProps({
    identifier: {
        type: String,
        default: '',
    },
    movement: {
        type: Object,
        default: () => ({}),
    },
    evidence: {
        type: Object,
        default: () => ({}),
    },
    type: {
        type: String,
        default: '',
    },
    modalFor: {
        type: String,
        default: '',
    }
});

const emit = defineEmits(['evidence-created', 'file-deleted']);

const files = computed(() => props.evidence?.files ?? []);

const fileInternalSignatures = ref([]);
const fileUpload = ref(null);

const action = ref('');
const damage = ref('');
const startingMileage = ref('');
const startingFuel = ref(0);
const printName = ref('');

const jobLink = computed(() => route('movements.jobs.show', { job: props.movement?.job?.id }));
const movementLink = computed(() => route('movements.movements.show', { movement: props.movement?.id }));
const customerLink = computed(() => route('movements.customers.show', { customer: props.movement?.job?.customer?.id }));
const driverLink = computed(() => props.movement?.driver ? route('movements.drivers.show', { driver: props.movement?.driver?.id }) : '');
const vehicleLink = computed(() => route('movements.vehicles.show', { vehicle: props.movement?.job?.vehicle?.id }));
const fileUploadRoute = computed(() => route('api.movements.evidence.get-upload-url'));

const modal = ref({});

const hasEvidence = computed(() =>
    Array.isArray(props.movement?.evidence) &&
    props.movement?.evidence.some(e => e.type === props.evidence?.type)
);

const getModalType = () => {
    return props.modalFor !== '' ? props.modalFor + '_' : '';
}

const isLastMovement = computed( () => {
    const { movements } = props.movement?.job ?? {};
    const last = movements?.[movements.length - 1];
    return !!last && last.id === props.movement?.id;
});

const phaseLabel = computed(() => {
    if (props.type !== 'pod') return 'Starting';
    return isLastMovement.value ? 'End' : 'Finish';
});

const getFuelLabel = computed(() => `${phaseLabel.value} Fuel`);
const getMileageLabel = computed(() => `${phaseLabel.value} Mileage`);

const handleFileUploaded = (signatures) => {
    fileInternalSignatures.value = signatures;
}

const createNewEvidence = () => {

    let type = props.type.toLowerCase();
    if ( props.evidence?.type ) {
        type = props.evidence?.type.toLowerCase();
    }

    const data = {
        movement_id: props.movement?.id,
        driver_id: props.movement?.driver?.id ?? null,
        evidence_id: props.evidence?.id ?? null,
        type: type ?? null,
        print_name: printName.value,
        action: action.value,
        damage: damage.value,
        mileage: startingMileage.value,
        fuel: startingFuel.value,
        file_internal_signature: fileInternalSignatures.value || null,
    }

    const url = hasEvidence.value
        ? route('api.movements.evidence.update', { evidence: props.evidence?.id })
        : route('api.movements.evidence.store');

    const method = hasEvidence.value ? 'put' : 'post';

    axios[method](url, data, {
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    })
        .then(response => {

            emit('evidence-created', response.data);

            fileUpload.value.reset();
            fileInternalSignatures.value = [];

            const message = hasEvidence.value
                ? 'Evidence Updated'
                : 'Evidence Created';

            successAlert(message, 'Success');

            // Bootstrap .hide() never works and at this point it's easier to use what works
            window.jQuery('.modal-header .btn-close').click();
        })
        .catch(error => {
            errorAlert(error, 'Error Creating Evidence');
        });
}

const deleteFile = (file) => {

    if (!confirm('Are you sure you want to delete this file?')) {
        return;
    }

    axios.delete(route('api.movements.evidence.files.destroy', {file: file.id}))
        .then((response) => {
            successAlert('File Deleted', 'Success');
            emit('file-deleted', response.data);
        })
        .catch((error) => {
            errorAlert(error, "Error Deleting File");
        });
}

const getFileLink = (file) => {
    if (!file) {
        return '';
    }
    return route('movements.evidence.files.show', {file: file.id});
}

const autofillEvidence = () => {
    action.value = '<>';
    damage.value = '<>';
    startingMileage.value = 0;
    startingFuel.value = 0;
    printName.value = '<>';

    createNewEvidence();
}

const compModalIdentifier = computed(() => {
    return props.identifier + '_evidence_modal';
});

onMounted(() => {
    modal.value = new Modal(document.getElementById(compModalIdentifier.value));
});

watch(() => props.evidence, newEvidence => {
    printName.value      = newEvidence?.print_name      || ''
    action.value         = newEvidence?.action          || ''
    damage.value         = newEvidence?.damage          || ''
    startingMileage.value= newEvidence?.mileage         ?? null
    startingFuel.value   = newEvidence?.fuel            ?? null
}, { immediate: true })
</script>
